import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { normalizeGameId } from '../game';
import { playerDisplayName } from '../game/presentation';
import { dispatchGameAction } from '../services/gameRepository';
import { humanizeFirebaseError } from '../hooks/useGame';

const JoinGame: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [playerName, setPlayerName] = useState(playerDisplayName(currentUser));
  const [gameId, setGameId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const autoJoined = useRef(false);

  const join = async (name: string, id: string) => {
    if (!currentUser) {
      navigate('/login', { state: { from: location } });
      return;
    }
    try {
      setLoading(true);
      setError('');
      await dispatchGameAction(id, { type: 'JOIN', playerId: currentUser.uid, playerName: name });
      navigate(`/game-lobby/${id}`);
    } catch (err) {
      setError(humanizeFirebaseError(err));
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('gameId') || params.get('code');
    if (fromQuery) {
      setGameId(normalizeGameId(fromQuery));
    }
    if (currentUser?.displayName) {
      setPlayerName(currentUser.displayName);
    }
  }, [location, currentUser]);

  useEffect(() => {
    if (autoJoined.current || !currentUser || loading) return;
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('gameId') || params.get('code');
    if (!fromQuery) return;
    const id = normalizeGameId(fromQuery);
    const name = (playerName.trim() || playerDisplayName(currentUser)).trim();
    if (id.length < 4 || !name) return;
    autoJoined.current = true;
    void join(name, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, location.search, playerName]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = playerName.trim();
    const id = normalizeGameId(gameId);
    if (!name) {
      setError('Enter your name.');
      return;
    }
    if (id.length < 4) {
      setError('Enter the 6-character code from the host.');
      return;
    }
    await join(name, id);
  };

  return (
    <div className="ss-sheet">
      <p className="ss-kicker">Guest</p>
      <h1 className="ss-title">Join a game</h1>
      <p className="ss-sub">Enter the code your host shared, or open their invite link.</p>

      {error && <div className="ss-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <label htmlFor="playerName" className="ss-label">
          Your name
        </label>
        <input
          id="playerName"
          className="ss-input"
          value={playerName}
          onChange={(event) => setPlayerName(event.target.value)}
          placeholder="Sam"
          autoComplete="nickname"
          required
        />

        <label htmlFor="gameId" className="ss-label">
          Game code
        </label>
        <input
          id="gameId"
          className="ss-input ss-code"
          value={gameId}
          onChange={(event) => setGameId(normalizeGameId(event.target.value))}
          placeholder="ABC123"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          required
        />

        <div className="ss-row">
          <button type="button" className="ss-btn ss-btn-ghost" onClick={() => navigate('/')}>
            Back
          </button>
          <button type="submit" className="ss-btn ss-btn-primary" disabled={loading}>
            {loading ? 'Joining…' : 'Join'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JoinGame;
